import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
   Card, Tag, Form, Input, Button, DatePicker,
   Table, message, Row, Col, Timeline, Spin,
   Avatar, Space, Tooltip, Badge, Divider, Empty
} from "antd";
import {
   UserOutlined, CheckCircleOutlined, SyncOutlined,
   ClockCircleOutlined, EditOutlined, PlusOutlined,
   PhoneOutlined, MailOutlined, GlobalOutlined,
   CalendarOutlined, ArrowLeftOutlined, HistoryOutlined,
   FileTextOutlined, WarningOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
   getLeadDetails,
   getLeadFollowups,
   getLeadActivities,
   addFollowup
} from "../../services/leadDetailsApi";

dayjs.extend(relativeTime);

const LeadDetails = () => {
   const { id } = useParams();
   const navigate = useNavigate();

   const [lead, setLead] = useState(null);
   const [followups, setFollowups] = useState([]);
   const [activities, setActivities] = useState([]);

   const [loading, setLoading] = useState(false);
   const [tableLoading, setTableLoading] = useState(false);
   const [timelineLoading, setTimelineLoading] = useState(true);

   const [followupRefresh, setFollowupRefresh] = useState(0);
   const [activityRefresh, setActivityRefresh] = useState(0);

   const [form] = Form.useForm();

   const getTimelineIcon = (action) => {
      if (action.includes("Created"))
         return <UserOutlined style={{ color: "#1890ff" }} />;
      if (action.includes("Assigned"))
         return <SyncOutlined style={{ color: "#722ed1" }} />;
      if (action.includes("Follow-up"))
         return <ClockCircleOutlined style={{ color: "#fa8c16" }} />;
      if (action.includes("Converted"))
         return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      if (action.includes("Updated"))
         return <EditOutlined style={{ color: "#13c2c2" }} />;
      return <UserOutlined />;
   };

   const statusConfig = {
      "New": { color: "blue", icon: <PlusOutlined /> },
      "Contacted": { color: "orange", icon: <PhoneOutlined /> },
      "Qualified": { color: "purple", icon: <CheckCircleOutlined /> },
      "Proposal Sent": { color: "cyan", icon: <FileTextOutlined /> },
      "Closed Won": { color: "green", icon: <CheckCircleOutlined /> },
      "Closed Lost": { color: "red", icon: <ClockCircleOutlined /> }
   };

   // ---------------- FETCH LEAD ----------------
   useEffect(() => {
      getLeadDetails(id)
         .then(res => setLead(res.data))
         .catch(err => console.log(err));
   }, [id]);

   // ---------------- FETCH FOLLOWUPS ----------------
   useEffect(() => {
      setTableLoading(true);
      getLeadFollowups(id)
         .then(res => setFollowups(res.data))
         .catch(err => console.log(err))
         .finally(() => setTableLoading(false));
   }, [id, followupRefresh]);

   // ---------------- FETCH ACTIVITIES ----------------
   useEffect(() => {
      setTimelineLoading(true);
      getLeadActivities(id)
         .then(res => setActivities(res.data))
         .catch(err => console.log(err))
         .finally(() => setTimelineLoading(false));
   }, [id, activityRefresh]);

   // ---------------- ADD FOLLOWUP ----------------
   const handleAddFollowup = (values) => {
      setLoading(true);
      addFollowup({
         lead_id: id,
         note: values.note,
         next_followup_date: values.next_followup_date.format("YYYY-MM-DD")
      })
         .then(() => {
            form.resetFields();
            setFollowupRefresh(prev => prev + 1);
            setActivityRefresh(prev => prev + 1);
            message.success("Follow-up added successfully!");
         })
         .catch(() => {
            message.error("Failed to add follow-up");
         })
         .finally(() => {
            setLoading(false);
         });
   };

   // ---------------- FOLLOWUP TABLE COLUMNS ----------------
   const followupColumns = [
      {
         title: <span className="text-xs font-medium text-gray-500">#</span>,
         width: 50,
         align: "center",
         render: (_, __, index) => (
            <span className="text-xs text-gray-400">{index + 1}</span>
         )
      },
      {
         title: <span className="text-xs font-medium text-gray-600">Created By</span>,
         dataIndex: "created_by_name",
         width: 140,
         render: (name) => (
            <div className="flex items-center gap-2">
               <span className="text-sm text-gray-700">{name}</span>
            </div>
         )
      },
      {
         title: <span className="text-xs font-medium text-gray-600">Note</span>,
         dataIndex: "note",
         width: 250,  // Fixed width
         ellipsis: {
            showTitle: false,  // Disable default title
         },
         render: (note) => (
            <Tooltip title={note} placement="topLeft">
               <span className="text-sm text-gray-600">
                  {note}
               </span>
            </Tooltip>
         )
      },
      {
         title: <span className="text-xs font-medium text-gray-600">Follow-up Date</span>,
         dataIndex: "next_followup_date",
         width: 200,
         render: (date) => {
            const today = dayjs().startOf("day");
            const followupDate = dayjs(date);
            let config = { color: "green", text: "Upcoming", icon: <CalendarOutlined /> };

            if (followupDate.isBefore(today)) {
               config = { color: "red", text: "Overdue", icon: <WarningOutlined /> };
            } else if (followupDate.isSame(today, "day")) {
               config = { color: "orange", text: "Today", icon: <ClockCircleOutlined /> };
            }

            return (
               <div className="flex items-center gap-2">
                  <span className="text-sm">{followupDate.format("DD MMM YYYY")}</span>
                  <Tag
                     color={config.color}
                     style={{ margin: 0, fontSize: '11px' }}
                     icon={config.icon}
                  >
                     {config.text}
                  </Tag>
               </div>
            );
         }
      },
      {
         title: <span className="text-xs font-medium text-gray-600">Created</span>,
         dataIndex: "created_at",
         width: 120,
         render: (date) => (
            <span className="text-xs text-gray-500">
               {dayjs(date).fromNow()}
            </span>
         )
      }
   ];

   // ---------------- ROW COLOR ----------------
   const getRowClassName = (record) => {
      const today = dayjs().startOf("day");
      const followupDate = dayjs(record.next_followup_date);

      if (followupDate.isBefore(today))
         return "bg-red-50 hover:bg-red-100";
      if (followupDate.isSame(today, "day"))
         return "bg-yellow-50 hover:bg-yellow-100";
      return "";
   };

   if (!lead)
      return (
         <div className="flex justify-center items-center h-60">
            <Spin size="large" />
         </div>
      );

   const statusInfo = statusConfig[lead.status];
   const overdueCount = followups.filter(f =>
      dayjs(f.next_followup_date).isBefore(dayjs().startOf("day"))
   ).length;

   return (
      <div className="bg-gray-50 min-h-screen">
         {/* Custom Styles */}
         <style>{`
            .lead-card {
               background: #fff;
               border-radius: 10px;
               box-shadow: 0 1px 2px 0 rgba(0,0,0,0.03);
               border: 1px solid #f3f4f6;
            }
            .lead-table .ant-table-thead > tr > th {
               background: #f9fafb;
               font-size: 12px;
               padding: 10px 16px;
            }
            .lead-table .ant-table-tbody > tr > td {
               padding: 10px 16px;
               font-size: 13px;
            }
         `}</style>

         {/* Header */}
         <div className="bg-white border-b px-6 py-4">
            <div className="flex items-center justify-between">
               <div className="flex items-center gap-4">
                  <Button
                     type="text"
                     icon={<ArrowLeftOutlined />}
                     onClick={() => navigate('/leads')}
                  >
                     Back
                  </Button>
                  <Divider type="vertical" className="h-6" />
                  <div>
                     <h1 className="text-xl font-semibold text-gray-800 m-0">
                        Lead Details
                     </h1>
                     <span className="text-gray-400 text-sm">
                        View and manage lead information
                     </span>
                  </div>
               </div>
            </div>
         </div>

         <div className="p-6 space-y-4">
            {/* Lead Overview Card */}
            <div className="lead-card p-6">
               <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                     <Avatar
                        size={64}
                        style={{ backgroundColor: '#6366f1' }}
                        className="shadow-sm"
                     >
                        {lead.name?.charAt(0)?.toUpperCase()}
                     </Avatar>
                     <div>
                        <h2 className="text-2xl font-semibold text-gray-800 m-0">
                           {lead.name}
                        </h2>
                        <div className="flex items-center gap-4 mt-2">

                           <Space>
                              <PhoneOutlined className="text-gray-400" />
                              <span className="text-sm text-gray-600">{lead.phone || 'N/A'}</span>
                           </Space>
                           <Divider type="vertical" />

                        </div>
                     </div>
                  </div>
                  <Tag
                     color={statusInfo?.color}
                     style={{ fontSize: '13px', padding: '4px 12px' }}
                     icon={statusInfo?.icon}
                  >
                     {lead.status}
                  </Tag>
               </div>

               {/* Stats */}
               <Row gutter={[16, 16]}>
                  <Col xs={8}>
                     <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-800">
                           {followups.length}
                        </div>
                        <div className="text-xs text-gray-500">Total Follow-ups</div>
                     </div>
                  </Col>
                  <Col xs={8}>
                     <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">
                           {overdueCount}
                        </div>
                        <div className="text-xs text-gray-500">Overdue</div>
                     </div>
                  </Col>
                  <Col xs={8}>
                     <div className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-800">
                           {activities.length}
                        </div>
                        <div className="text-xs text-gray-500">Activities</div>
                     </div>
                  </Col>
               </Row>
            </div>

            {/* Add Follow-up Card */}
            <div className="lead-card">
               <div className="p-4 border-b">
                  <div className="flex items-center gap-2">
                     <PlusOutlined className="text-gray-600" />
                     <span className="font-medium text-gray-700">Add Follow-up</span>
                  </div>
               </div>
               <div className="p-4">
                  <Form form={form} onFinish={handleAddFollowup} layout="vertical">
                     <Row gutter={16} align="bottom">
                        <Col xs={24} md={14}>
                           <Form.Item
                              name="note"
                              label={<span className="text-xs text-gray-600">Note</span>}
                              rules={[{ required: true, message: "Please enter note" }]}
                           >
                              <Input.TextArea
                                 rows={2}
                                 placeholder="Enter follow-up note..."
                              />
                           </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                           <Form.Item
                              name="next_followup_date"
                              label={<span className="text-xs text-gray-600">Follow-up Date</span>}
                              rules={[{ required: true, message: "Please select date" }]}
                           >
                              <DatePicker
                                 className="w-full"
                                 disabledDate={(current) =>
                                    current && current < dayjs().startOf('day')
                                 }
                              />
                           </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                           <Form.Item>
                              <Button
                                 type="primary"
                                 htmlType="submit"
                                 loading={loading}
                                 icon={<PlusOutlined />}
                                 className="w-full"
                              >
                                 Add Follow-up
                              </Button>
                           </Form.Item>
                        </Col>
                     </Row>
                  </Form>
               </div>
            </div>

            <Row gutter={[16, 16]}>
               {/* Follow-up History */}
               <Col xs={24} lg={14}>
                  <div className="lead-card">
                     <div className="p-4 border-b">
                        <div className="flex items-center justify-between">
                           <div className="flex items-center gap-2">
                              <HistoryOutlined className="text-gray-600" />
                              <span className="font-medium text-gray-700">
                                 Follow-up History
                              </span>
                              <Badge count={followups.length} showZero />
                           </div>
                        </div>
                     </div>
                     <div className="lead-table">
                        <Table
                           dataSource={followups}
                           columns={followupColumns}
                           rowKey="id"
                           loading={tableLoading}
                           rowClassName={getRowClassName}
                           pagination={{
                              pageSize: 5,
                              size: "small",
                              showTotal: (total) => (
                                 <span className="text-xs text-gray-500">
                                    Total {total} follow-ups
                                 </span>
                              )
                           }}
                           size="small"
                           scroll={{ x: 600 }}
                           locale={{
                              emptyText: (
                                 <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="No follow-ups yet"
                                 />
                              )
                           }}
                        />
                     </div>
                  </div>
               </Col>

               {/* Activity Timeline */}
               <Col xs={24} lg={10}>
                  <div className="lead-card">
                     <div className="p-4 border-b">
                        <div className="flex items-center justify-between">
                           <div className="flex items-center gap-2">
                              <ClockCircleOutlined className="text-gray-600" />
                              <span className="font-medium text-gray-700">
                                 Activity Timeline
                              </span>
                              <Badge count={activities.length} showZero />
                           </div>
                        </div>
                     </div>
                     <div className="p-4" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {timelineLoading ? (
                           <div className="py-8 text-center">
                              <Spin />
                           </div>
                        ) : activities.length === 0 ? (
                           <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="No activity yet"
                           />
                        ) : (
                           <Timeline mode="left">
                              {activities.map(item => (
                                 <Timeline.Item
                                    key={item.id}
                                    dot={getTimelineIcon(item.action)}
                                 >
                                    <div className="text-sm font-medium text-gray-800">
                                       {item.action}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                       <UserOutlined className="mr-1" />
                                       {item.user_name || "System"} • {" "}
                                       {dayjs(item.created_at).fromNow()}
                                    </div>
                                 </Timeline.Item>
                              ))}
                           </Timeline>
                        )}
                     </div>
                  </div>
               </Col>
            </Row>
         </div>
      </div>
   );
};

export default LeadDetails;